import React, { useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Input } from "@/components/Base/Input";
import { Label } from "@/components/Base/Label";
import { ContainerTimeConfig } from "@/types/v2/Metrics";
import { useMetricsConfigs } from "@/hooks/useMetricsConfigs";
import { Trash2, Save } from "lucide-react";

interface ConfigurationSelectorProps {
  currentConfig: Partial<ContainerTimeConfig>;
  onConfigLoad: (config: ContainerTimeConfig) => void;
}

export const ConfigurationSelector: React.FC<ConfigurationSelectorProps> = ({
  currentConfig,
  onConfigLoad,
}) => {
  const {
    configs,
    loading,
    saveContainerTimeConfig,
    deleteContainerTimeConfig,
  } = useMetricsConfigs();
  const [selectedConfigId, setSelectedConfigId] = useState<string>("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [configName, setConfigName] = useState("");

  const handleLoadConfig = (configId: string) => {
    const config = configs?.containerTimeConfigs.find((c) => c.id === configId);
    if (config) {
      onConfigLoad(config);
      setSelectedConfigId(configId);
    }
  };

  const handleSaveConfig = async () => {
    if (!configName.trim()) return;

    try {
      await saveContainerTimeConfig({
        name: configName.trim(),
        firstContainer: currentConfig.firstContainer || "",
        firstEvent: currentConfig.firstEvent || "enters",
        secondContainer: currentConfig.secondContainer || "",
        secondEvent: currentConfig.secondEvent || "exits",
        timeUnit: currentConfig.timeUnit || "hours",
        colorCoding: currentConfig.colorCoding || "none",
        chartType: currentConfig.chartType || "bar",
      });
      setSaveDialogOpen(false);
      setConfigName("");
    } catch (error) {
      console.error("Failed to save configuration:", error);
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (confirm("Are you sure you want to delete this configuration?")) {
      try {
        await deleteContainerTimeConfig(configId);
        if (selectedConfigId === configId) {
          setSelectedConfigId("");
        }
      } catch (error) {
        console.error("Failed to delete configuration:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading configurations...
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <Label htmlFor="config-select" className="text-sm font-medium">
          Configuration:
        </Label>
        <Select value={selectedConfigId} onValueChange={handleLoadConfig}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select a saved configuration" />
          </SelectTrigger>
          <SelectContent>
            {configs?.containerTimeConfigs.map((config) => (
              <SelectItem key={config.id} value={config.id}>
                <div className="flex w-full items-center justify-between">
                  <span>{config.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConfig(config.id);
                    }}
                    className="ml-2 h-6 w-6 p-0"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Save className="mr-2 h-4 w-4" />
            Save Configuration
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="config-name">Configuration Name</Label>
              <Input
                id="config-name"
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                placeholder="Enter configuration name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveConfig();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setSaveDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveConfig} disabled={!configName.trim()}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
